/**
 * Hook de integración MapViewer - V06.1
 * Gestiona estado de filtros, datos filtrados y selección
 * 
 * PTEL - Sistema de Normalización de Coordenadas
 */

import { useMemo, useState, useEffect, useCallback } from 'react';
import type { NormalizedInfrastructure, InfrastructureType } from '../../../types/infrastructure';
import type { MarkerGroup } from '../../../constants/markerConfig';
import {
  useFilterState,
  filterInfrastructures,
  calculateFilterStats,
  type FilterState,
  type FilterStatsType,
  type UseFilterStateOptions,
} from '../FilterPanel';

// =============================================================================
// TIPOS
// =============================================================================

export interface UseMapViewerOptions extends UseFilterStateOptions {
  /** Datos de infraestructuras a mostrar */
  data: NormalizedInfrastructure[];
  /** Código INE del municipio (para validación geográfica futura) */
  codigoINE?: string;
}

export interface UseMapViewerReturn {
  /** Datos después de aplicar filtros */
  filteredData: NormalizedInfrastructure[];
  /** Estadísticas de filtrado */
  stats: FilterStatsType;
  /** Estado actual de filtros */
  filters: FilterState;
  /** Actualizar filtros completos */
  setFilters: (filters: FilterState) => void;
  /** Acciones de filtrado individuales */
  filterActions: {
    toggleType: (type: InfrastructureType) => void;
    toggleGroup: (group: MarkerGroup) => void;
    toggleGroupExpanded: (group: MarkerGroup) => void;
    setScoreRange: (min: number, max: number) => void;
    setSearchText: (text: string) => void;
    resetFilters: () => void;
    selectAllTypes: () => void;
    deselectAllTypes: () => void;
  };
  /** Estado y control de selección */
  selection: {
    selected: NormalizedInfrastructure | null;
    select: (infra: NormalizedInfrastructure | null) => void;
    clear: () => void;
  };
  /** Metadata del visor */
  meta: {
    codigoINE?: string;
    isEmpty: boolean;
    isFiltered: boolean;
  };
}

// =============================================================================
// HOOK
// =============================================================================

export function useMapViewer(options: UseMapViewerOptions): UseMapViewerReturn {
  const { data, codigoINE, ...filterOptions } = options;

  // Hook de estado de filtros (V05)
  const {
    filters,
    setFilters,
    toggleType,
    toggleGroup,
    toggleGroupExpanded,
    setScoreRange,
    setSearchText,
    resetFilters,
    selectAllTypes,
    deselectAllTypes,
  } = useFilterState(filterOptions);

  // Datos filtrados (memoizado para evitar recálculos)
  const filteredData = useMemo(
    () => filterInfrastructures(data, filters),
    [data, filters]
  );

  // Estadísticas (memoizado)
  const stats = useMemo(
    () => calculateFilterStats(data, filteredData),
    [data, filteredData]
  );

  // Estado de selección
  const [selected, setSelected] = useState<NormalizedInfrastructure | null>(null);

  // Limpiar selección si el elemento ya no está en filteredData
  useEffect(() => {
    if (!selected || filteredData.find(d => d.id === selected.id)) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- limpieza de selección si elemento ya no existe; guarda explícita
    setSelected(null);
  }, [filteredData, selected]);

  // Handlers de selección
  const select = useCallback((infra: NormalizedInfrastructure | null) => {
    setSelected(infra);
  }, []);

  const clearSelection = useCallback(() => {
    setSelected(null);
  }, []);

  // Metadata
  const meta = useMemo(() => ({
    codigoINE,
    isEmpty: data.length === 0,
    isFiltered: filteredData.length < data.length,
  }), [codigoINE, data.length, filteredData.length]);

  return {
    filteredData,
    stats,
    filters,
    setFilters,
    filterActions: {
      toggleType,
      toggleGroup,
      toggleGroupExpanded,
      setScoreRange,
      setSearchText,
      resetFilters,
      selectAllTypes,
      deselectAllTypes,
    },
    selection: {
      selected,
      select,
      clear: clearSelection,
    },
    meta,
  };
}

export default useMapViewer;
